import axios from 'axios';
import { createSingleFlight } from '../singleFlight';

const client = axios.create({
  //baseURL: 'http://localhost:8080/',
  timeout: 180000,
  // 2026-10-19 (bito 2단계, ksm_frontend에서 포팅): 리프레시 토큰이 HttpOnly 쿠키(refresh_token,
  // path=/api/v1/user)로 오가므로 쿠키를 주고받으려면 true여야 한다(같은 origin이면 영향
  // 없지만, REACT_APP_API_URL로 직접 백엔드를 찍는 설정 등 교차 origin 상황에서도 동작하게 해둔다).
  withCredentials: true,
});

// 리프레시 호출 전용 — client의 요청 인터셉터(Authorization 재부착 등)를 타지 않는 별도 인스턴스.
// 리프레시는 쿠키만으로 인증하므로 Authorization 헤더가 없어도 된다(있어도 서버가 무시함).
const refreshClient = axios.create({
  timeout: 15000,
  withCredentials: true,
});

const REFRESH_URL = '/api/v1/user/token/refresh';

/**
 * 동시에 여러 요청이 401을 맞아도 리프레시 호출은 한 번만 나가게 하는 single-flight
 * (createSingleFlight는 axios와 분리된 순수 유틸 - src/lib/singleFlight.test.js에서 단위테스트함).
 * 진행 중인 리프레시가 있으면 그 Promise를 그대로 돌려준다.
 */
const requestRefresh = createSingleFlight(async () => {
  const res = await refreshClient.post(REFRESH_URL);
  const newToken = res?.data?.data;
  if (!newToken) {
    throw new Error('리프레시 응답에 액세스 토큰이 없습니다.');
  }
  // 기존 localStorage 키를 그대로 쓴다 — 다른 코드(axios 호출 등)가 안 바뀌어도 된다.
  localStorage.setItem('access_token', JSON.stringify(newToken));
  window.dispatchEvent(new CustomEvent('bito:token-refreshed', { detail: newToken }));
  return newToken;
});

function clearSessionAndGoToLogin() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
  window.location.href = '/login';
}

client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    const user = localStorage.getItem('user');
    //let targetUrl = 'http://127.0.0.1:8080/api';

    if (token) {
      //console.log('Bearer ' + JSON.parse(token));
      config.headers['Authorization'] = 'Bearer ' + JSON.parse(token);
    }
    if (user) {
      // 로그인하면 해당하는 url 에 따라야 함.
      const jsonUser = JSON.parse(user);
      config.headers['companyCd'] = jsonUser.companyCd;
    }
    // config.headers['x-target-base-endpoint'] = targetUrl;

    // FormData인 경우 Content-Type을 설정하지 않음 (axios가 자동으로 multipart/form-data + boundary 설정)
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    // console.log(config.headers, 'config.headers');
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

client.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.log('client.js', error);

    const config = error.config;
    const status = error.response ? error.response.status : null;
    const isRefreshCall = config && typeof config.url === 'string' && config.url.includes(REFRESH_URL);

    // 2026-10-19 (ksm_frontend에서 포팅): 액세스 토큰 만료(401)는 바로 로그아웃시키지 않고,
    // 리프레시를 한 번 시도해서 성공하면 원래 요청을 새 토큰으로 재시도한다. 리프레시 자체가
    // 401이거나 이미 한 번 재시도한 요청이면(무한루프 방지) 그대로 로그인 화면으로 보낸다.
    if (status === 401 && !isRefreshCall && config && !config._retriedAfterRefresh) {
      config._retriedAfterRefresh = true;
      try {
        const newToken = await requestRefresh();
        config.headers = config.headers || {};
        config.headers['Authorization'] = 'Bearer ' + newToken;
        return client(config);
      } catch (refreshError) {
        clearSessionAndGoToLogin();
        return Promise.reject(refreshError);
      }
    }

    if (status === 401 || status === 504) {
      clearSessionAndGoToLogin();
    }

    return Promise.reject(error);
  },
);

export { requestRefresh, clearSessionAndGoToLogin };

export default client;
